<template>
  <div class="savings-view-container">
    <MainHeader :title="'Savings Goal'">
      <template #left>
        <font-awesome-icon
          @click="router.back()"
          :icon="['fas', 'arrow-left-long']"
          class="back-arrow pointer"
          role="button"
          aria-label="Go back"
        />
      </template>
    </MainHeader>

    <div class="view content content-padding">
      <div class="intro-card">
        <div class="intro-icon">
          <font-awesome-icon :icon="['fas', 'piggy-bank']" />
        </div>
        <p class="intro-text">
          Plan a target by date, by contribution, or by what's realistic. You
          decide which one is fixed.
        </p>
      </div>

      <div class="ud-section">
        <span class="section-label">Goal</span>

        <div class="wrapper-card">
          <div class="ud-field">
            <label class="ud-label">What are you saving for?</label>
            <input
              v-model="goalName"
              type="text"
              class="input"
              placeholder="Emergency fund, Hawaii trip, new roof"
              maxlength="60"
            />
          </div>

          <div class="ud-field mt">
            <label class="ud-label">Target amount</label>
            <div class="currency-input">
              <span class="currency-symbol">$</span>
              <input
                v-model.number="targetAmount"
                type="number"
                class="input currency-field"
                placeholder="0"
                inputmode="decimal"
                min="0"
                step="100"
              />
            </div>
          </div>

          <div class="ud-field mt">
            <label class="ud-label">
              Already saved
              <span class="optional-tag">optional</span>
            </label>
            <div class="currency-input">
              <span class="currency-symbol">$</span>
              <input
                v-model.number="startingBalance"
                type="number"
                class="input currency-field"
                placeholder="0"
                inputmode="decimal"
                min="0"
                step="100"
              />
            </div>
          </div>

          <div class="ud-field mt">
            <label class="ud-label">
              Interest rate, APY
              <span class="optional-tag">optional</span>
            </label>
            <div class="percent-input">
              <input
                v-model.number="apyPercent"
                type="number"
                class="input percent-field"
                placeholder="0"
                inputmode="decimal"
                step="0.1"
                min="0"
                max="20"
              />
              <span class="percent-symbol">%</span>
            </div>
            <p class="field-hint">
              High-yield savings, money market, or CD rate.
            </p>
          </div>
        </div>
      </div>

      <div class="ud-section">
        <span class="section-label">What do you want to figure out?</span>

        <div class="solve-grid">
          <button
            v-for="mode in solveModes"
            :key="mode.value"
            type="button"
            class="solve-card"
            :class="{ selected: solveMode === mode.value }"
            @click="solveMode = mode.value"
          >
            <div class="solve-icon-wrap" :class="mode.tone">
              <font-awesome-icon :icon="['fas', mode.icon]" />
            </div>
            <p class="solve-name">{{ mode.title }}</p>
            <p class="solve-desc">{{ mode.desc }}</p>
          </button>
        </div>
      </div>

      <div class="ud-section">
        <span class="section-label">{{ inputsLabel }}</span>

        <div class="wrapper-card">
          <div class="ud-field" ref="freqDropdownRef">
            <label class="ud-label">Contribution frequency</label>
            <button
              type="button"
              class="form-select grey-outline-form-select category-trigger"
              :class="{ open: freqDropdownOpen }"
              @click="freqDropdownOpen = !freqDropdownOpen"
            >
              <span class="category-trigger-value">
                {{ selectedFreqLabel }}
              </span>
              <font-awesome-icon
                :icon="['fas', 'chevron-down']"
                class="category-chevron"
                :class="{ open: freqDropdownOpen }"
              />
            </button>
            <transition name="sub-slide">
              <div v-if="freqDropdownOpen" class="category-menu">
                <button
                  v-for="opt in frequencyOptions"
                  :key="opt.value"
                  type="button"
                  class="category-option"
                  :class="{ selected: payFrequency === opt.value }"
                  @click="selectFrequency(opt.value)"
                >
                  <span class="option-name">{{ opt.label }}</span>
                  <span class="option-remaining">{{ opt.sub }}</span>
                </button>
              </div>
            </transition>
          </div>

          <div v-if="solveMode === 'amount'" class="ud-field mt">
            <label class="ud-label">Target date</label>
            <input
              v-model="targetDate"
              type="date"
              class="input"
              :min="minDate"
            />
          </div>

          <div v-if="solveMode === 'date'" class="ud-field mt">
            <label class="ud-label"> Contribution per {{ periodWord }} </label>
            <div class="currency-input">
              <span class="currency-symbol">$</span>
              <input
                v-model.number="contributionPerPeriod"
                type="number"
                class="input currency-field"
                placeholder="0"
                inputmode="decimal"
                min="0"
                step="10"
              />
            </div>
          </div>

          <template v-if="solveMode === 'goal'">
            <div class="ud-field mt">
              <label class="ud-label">
                Contribution per {{ periodWord }}
              </label>
              <div class="currency-input">
                <span class="currency-symbol">$</span>
                <input
                  v-model.number="contributionPerPeriod"
                  type="number"
                  class="input currency-field"
                  placeholder="0"
                  inputmode="decimal"
                  min="0"
                  step="10"
                />
              </div>
            </div>
            <div class="ud-field mt">
              <label class="ud-label">For how many months?</label>
              <input
                v-model.number="goalMonths"
                type="number"
                class="input"
                placeholder="0"
                inputmode="numeric"
                min="1"
                max="600"
                step="1"
              />
            </div>
          </template>
        </div>
      </div>

      <div class="ud-section">
        <span class="section-label">Funding source</span>

        <div class="wrapper-card">
          <div class="ud-field" ref="budgetDropdownRef">
            <label class="ud-label">
              Pull this savings from a budget category
              <span class="optional-tag">optional</span>
            </label>
            <button
              type="button"
              class="form-select grey-outline-form-select category-trigger"
              :class="{ open: budgetDropdownOpen }"
              @click="budgetDropdownOpen = !budgetDropdownOpen"
              :disabled="loadingBudget"
            >
              <span
                class="category-trigger-value"
                :class="{ placeholder: !selectedBudgetNode }"
              >
                {{ budgetTriggerLabel }}
              </span>
              <font-awesome-icon
                :icon="['fas', 'chevron-down']"
                class="category-chevron"
                :class="{ open: budgetDropdownOpen }"
              />
            </button>

            <transition name="sub-slide">
              <div v-if="budgetDropdownOpen" class="category-menu">
                <button
                  type="button"
                  class="category-option"
                  :class="{ selected: !selectedBudgetNode }"
                  @click="selectBudgetNone"
                >
                  <span class="option-name">— None / not budgeted —</span>
                </button>
                <template
                  v-for="cat in spendingCategories"
                  :key="cat.categoryID"
                >
                  <button
                    type="button"
                    class="category-option category-parent"
                    :class="{
                      selected:
                        selectedBudgetNode &&
                        selectedBudgetNode.type === 'category' &&
                        selectedBudgetNode.id === cat.categoryID,
                    }"
                    @click="selectBudgetCategory(cat)"
                  >
                    <span class="option-name">{{ cat.name }}</span>
                    <span
                      v-if="parentDisplayAvailable(cat) !== null"
                      class="option-remaining"
                    >
                      ${{ formatNumber(parentDisplayAvailable(cat)) }} left
                    </span>
                  </button>
                  <button
                    v-for="sub in cat.subcategories || []"
                    :key="sub.subcategoryID"
                    type="button"
                    class="category-option category-sub"
                    :class="{
                      selected:
                        selectedBudgetNode &&
                        selectedBudgetNode.type === 'subcategory' &&
                        selectedBudgetNode.id === sub.subcategoryID,
                    }"
                    @click="selectBudgetSubcategory(cat, sub)"
                  >
                    <span class="sub-indent">↳</span>
                    <span class="option-name">{{ sub.name }}</span>
                    <span class="option-remaining">
                      ${{ formatNumber(sub.available) }} left
                    </span>
                  </button>
                </template>
                <div
                  v-if="!loadingBudget && spendingCategories.length === 0"
                  class="category-empty"
                >
                  No budget categories yet for this month.
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>

      <div v-if="result.valid" class="ud-section">
        <span class="section-label">Your Plan</span>

        <div class="results-card" :class="resultToneClass">
          <div class="result-eyebrow">{{ resultEyebrow }}</div>
          <div class="result-main">{{ resultMainText }}</div>
          <div v-if="resultSecondary" class="result-sub">
            {{ resultSecondary }}
          </div>
        </div>

        <div v-if="signals.length" class="signals-list">
          <div
            v-for="(sig, i) in signals"
            :key="i"
            class="signal-row"
            :class="`signal-${sig.tone}`"
          >
            <font-awesome-icon :icon="['fas', sig.icon]" class="signal-icon" />
            <div class="signal-text">
              <span class="signal-title">{{ sig.title }}</span>
              <span class="signal-detail">{{ sig.detail }}</span>
            </div>
          </div>
        </div>

        <div class="wrapper-card mt milestones-card">
          <button
            type="button"
            class="expandable-header"
            @click="showMilestones = !showMilestones"
          >
            <span class="expandable-title">Milestones</span>
            <font-awesome-icon
              :icon="['fas', 'chevron-down']"
              class="expandable-chevron"
              :class="{ open: showMilestones }"
            />
          </button>
          <transition name="sub-slide">
            <div v-if="showMilestones" class="milestones-body">
              <div
                v-for="m in result.milestones"
                :key="m.pct"
                class="milestone-row"
              >
                <div class="milestone-info">
                  <span class="milestone-name">{{ m.pct }}% of goal</span>
                  <span class="milestone-meta">
                    ${{ formatNumber(m.amount) }}
                  </span>
                </div>
                <span
                  class="milestone-date"
                  :class="{ 'milestone-date-unpaid': !m.reached }"
                >
                  {{ m.dateLabel }}
                </span>
              </div>
            </div>
          </transition>
        </div>

        <div class="wrapper-card mt milestones-card">
          <button
            type="button"
            class="expandable-header"
            @click="showSchedule = !showSchedule"
          >
            <span class="expandable-title">Month-by-month schedule</span>
            <font-awesome-icon
              :icon="['fas', 'chevron-down']"
              class="expandable-chevron"
              :class="{ open: showSchedule }"
            />
          </button>
          <transition name="sub-slide">
            <div v-if="showSchedule" class="schedule-body">
              <div class="schedule-table-wrap">
                <table class="schedule-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th class="text-right">Added</th>
                      <th class="text-right">Interest</th>
                      <th class="text-right">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in result.schedule"
                      :key="row.month"
                      :class="{ 'schedule-milestone': row.isMilestone }"
                    >
                      <td>{{ row.label }}</td>
                      <td class="text-right">
                        ${{ formatNumber(row.contributed) }}
                      </td>
                      <td class="text-right">
                        ${{ formatNumber(row.interest) }}
                      </td>
                      <td class="text-right">
                        <strong>${{ formatNumber(row.balance) }}</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </transition>
        </div>

        <div v-if="result.totalInterest > 0" class="interest-summary mt">
          <div class="interest-row">
            <span>You contribute</span>
            <strong>${{ formatNumber(result.totalContributed) }}</strong>
          </div>
          <div class="interest-row interest-earned">
            <span>Interest earned</span>
            <strong>+${{ formatNumber(result.totalInterest) }}</strong>
          </div>
          <div class="interest-row interest-final">
            <span>Final balance</span>
            <strong>${{ formatNumber(result.finalBalance) }}</strong>
          </div>
        </div>
      </div>

      <div v-else class="empty-card">
        <p class="empty-text">{{ emptyMessage }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useMyStore } from '../../store';
import MainHeader from '../../components/View-Header.vue';

export default {
  components: { MainHeader },
  setup() {
    const router = useRouter();
    const store = useMyStore();

    const goalName = ref('');
    const targetAmount = ref(0);
    const startingBalance = ref(0);
    const apyPercent = ref(0);

    const solveMode = ref('amount');
    const solveModes = [
      {
        value: 'amount',
        title: 'How much per paycheck?',
        desc: 'I have a target date.',
        icon: 'calendar-day',
        tone: 'purple',
      },
      {
        value: 'date',
        title: 'When will I get there?',
        desc: 'I know what I can save.',
        icon: 'hourglass-half',
        tone: 'teal',
      },
      {
        value: 'goal',
        title: "What's realistic?",
        desc: 'Both contribution and time are fixed.',
        icon: 'scale-balanced',
        tone: 'coral',
      },
    ];

    const payFrequency = ref('biweekly');
    const freqDropdownOpen = ref(false);
    const freqDropdownRef = ref(null);
    const frequencyOptions = [
      { value: 'weekly', label: 'Weekly', sub: '52 per year', perYear: 52 },
      {
        value: 'biweekly',
        label: 'Every two weeks',
        sub: '26 per year',
        perYear: 26,
      },
      {
        value: 'semimonthly',
        label: 'Twice a month',
        sub: '24 per year',
        perYear: 24,
      },
      { value: 'monthly', label: 'Monthly', sub: '12 per year', perYear: 12 },
    ];
    const selectedFreqLabel = computed(
      () =>
        frequencyOptions.find((f) => f.value === payFrequency.value)?.label ||
        ''
    );
    const periodsPerYear = computed(
      () =>
        frequencyOptions.find((f) => f.value === payFrequency.value)?.perYear ||
        26
    );
    const periodWord = computed(() => {
      const map = {
        weekly: 'week',
        biweekly: 'paycheck',
        semimonthly: 'paycheck',
        monthly: 'month',
      };
      return map[payFrequency.value];
    });
    const selectFrequency = (val) => {
      payFrequency.value = val;
      freqDropdownOpen.value = false;
    };

    const targetDate = ref('');
    const contributionPerPeriod = ref(0);
    const goalMonths = ref(0);

    const minDate = computed(() => {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      return d.toISOString().slice(0, 10);
    });

    const inputsLabel = computed(() => {
      if (solveMode.value === 'amount') return 'Your timeline';
      if (solveMode.value === 'date') return 'Your contribution';
      return 'Your plan';
    });

    const categoryBudgets = ref([]);
    const loadingBudget = ref(false);
    const selectedBudgetNode = ref(null);
    const budgetDropdownOpen = ref(false);
    const budgetDropdownRef = ref(null);

    const fetchMonthlyBudget = async () => {
      loadingBudget.value = true;
      try {
        const today = new Date();
        const month = today.getMonth() + 1;
        const year = today.getFullYear();
        const response = await store.getMonthlyBudgetDataForAllCategories(
          month,
          year
        );
        categoryBudgets.value = response?.data?.categoryBudgets || [];
      } catch (e) {
        console.error('Failed to load monthly budget', e);
      } finally {
        loadingBudget.value = false;
      }
    };

    const spendingCategories = computed(() =>
      categoryBudgets.value
        .filter((c) => !c.income)
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
    );

    const parentDisplayAvailable = (cat) => {
      if (cat.subcategories && cat.subcategories.length > 0) {
        return cat.incSubAvailable;
      }
      return cat.available;
    };

    const findBudgetSubcategory = (id) => {
      for (const cat of categoryBudgets.value) {
        const sub = (cat.subcategories || []).find(
          (s) => s.subcategoryID === id
        );
        if (sub) return { sub, parent: cat };
      }
      return null;
    };

    const selectedBudgetCategory = computed(() => {
      if (!selectedBudgetNode.value) return null;
      if (selectedBudgetNode.value.type === 'category') {
        return categoryBudgets.value.find(
          (c) => c.categoryID === selectedBudgetNode.value.id
        );
      }
      const f = findBudgetSubcategory(selectedBudgetNode.value.id);
      return f ? f.parent : null;
    });

    const selectedBudgetSubcategory = computed(() => {
      if (
        !selectedBudgetNode.value ||
        selectedBudgetNode.value.type !== 'subcategory'
      ) {
        return null;
      }
      const f = findBudgetSubcategory(selectedBudgetNode.value.id);
      return f ? f.sub : null;
    });

    const budgetTriggerLabel = computed(() => {
      if (loadingBudget.value && !selectedBudgetNode.value) {
        return 'Loading your budget…';
      }
      if (!selectedBudgetNode.value) return '— None / not budgeted —';
      if (selectedBudgetSubcategory.value) {
        return `${selectedBudgetCategory.value.name} › ${selectedBudgetSubcategory.value.name}`;
      }
      return selectedBudgetCategory.value?.name || '—';
    });

    const selectBudgetNone = () => {
      selectedBudgetNode.value = null;
      budgetDropdownOpen.value = false;
    };
    const selectBudgetCategory = (cat) => {
      selectedBudgetNode.value = { type: 'category', id: cat.categoryID };
      budgetDropdownOpen.value = false;
    };
    const selectBudgetSubcategory = (cat, sub) => {
      selectedBudgetNode.value = {
        type: 'subcategory',
        id: sub.subcategoryID,
      };
      budgetDropdownOpen.value = false;
    };

    const budgetAvailable = computed(() => {
      if (selectedBudgetSubcategory.value) {
        return selectedBudgetSubcategory.value.available;
      }
      if (selectedBudgetCategory.value) {
        return parentDisplayAvailable(selectedBudgetCategory.value);
      }
      return null;
    });

    const handleClickOutside = (e) => {
      if (
        budgetDropdownRef.value &&
        !budgetDropdownRef.value.contains(e.target)
      ) {
        budgetDropdownOpen.value = false;
      }
      if (freqDropdownRef.value && !freqDropdownRef.value.contains(e.target)) {
        freqDropdownOpen.value = false;
      }
    };

    const periodRate = computed(() => {
      const apy = (apyPercent.value || 0) / 100;
      if (apy <= 0) return 0;
      return Math.pow(1 + apy, 1 / periodsPerYear.value) - 1;
    });

    const periodsToDate = computed(() => {
      if (!targetDate.value) return null;
      const start = new Date();
      const end = new Date(targetDate.value + 'T00:00:00');
      const days = Math.max(0, (end - start) / (1000 * 60 * 60 * 24));
      const periodDays = 365 / periodsPerYear.value;
      return Math.max(1, Math.ceil(days / periodDays));
    });

    const solvePmt = (fv, pv, n, r) => {
      if (n <= 0) return null;
      const pvFv = pv * Math.pow(1 + r, n);
      const remaining = fv - pvFv;
      if (remaining <= 0) return 0;
      if (r === 0) return remaining / n;
      return (remaining * r) / (Math.pow(1 + r, n) - 1);
    };

    const solveN = (fv, pv, pmt, r) => {
      if (pv >= fv) return 0;
      if (pmt <= 0 && r === 0) return Infinity;
      if (r === 0) return Math.ceil((fv - pv) / pmt);
      const denom = pv * r + pmt;
      if (denom <= 0) return Infinity;
      const x = (fv * r + pmt) / denom;
      if (x <= 1) return Infinity;
      const n = Math.log(x) / Math.log(1 + r);
      return Math.ceil(n);
    };

    const buildSchedule = (pv, pmt, n, r, targetFv) => {
      const schedule = [];
      let balance = pv;
      let totalContributed = 0;
      let totalInterest = 0;
      const milestonePcts = [25, 50, 75, 100];
      const milestoneTargets = milestonePcts.map((p) => (targetFv * p) / 100);
      const milestonesHit = {};

      const periodsPerMonthRaw = periodsPerYear.value / 12;
      const startDate = new Date();
      let periodIdx = 0;
      let monthIdx = 0;
      let monthContributed = 0;
      let monthInterest = 0;

      const cap = Math.min(n, 600);

      while (periodIdx < cap) {
        const interest = balance * r;
        balance = balance + interest + pmt;
        monthInterest += interest;
        monthContributed += pmt;
        totalContributed += pmt;
        totalInterest += interest;
        periodIdx++;

        const expectedPeriodsByEndOfMonth = Math.round(
          (monthIdx + 1) * periodsPerMonthRaw
        );
        if (periodIdx >= expectedPeriodsByEndOfMonth || periodIdx === cap) {
          monthIdx++;
          const labelDate = new Date(startDate);
          labelDate.setMonth(labelDate.getMonth() + monthIdx);

          let isMilestone = false;
          milestonePcts.forEach((p, i) => {
            if (!milestonesHit[p] && balance >= milestoneTargets[i]) {
              milestonesHit[p] = {
                pct: p,
                amount: milestoneTargets[i],
                date: new Date(labelDate),
                month: monthIdx,
              };
              isMilestone = true;
            }
          });

          schedule.push({
            month: monthIdx,
            label: labelDate.toLocaleDateString(undefined, {
              month: 'short',
              year: 'numeric',
            }),
            contributed: monthContributed,
            interest: monthInterest,
            balance,
            isMilestone,
          });
          monthContributed = 0;
          monthInterest = 0;
        }
      }

      const milestonesArr = milestonePcts.map((p) => {
        const hit = milestonesHit[p];
        return {
          pct: p,
          amount: (targetFv * p) / 100,
          reached: !!hit,
          dateLabel: hit
            ? hit.date.toLocaleDateString(undefined, {
                month: 'short',
                year: 'numeric',
              })
            : 'Beyond plan',
        };
      });

      return {
        schedule,
        totalContributed,
        totalInterest,
        finalBalance: balance,
        milestones: milestonesArr,
      };
    };

    const result = computed(() => {
      const fv = Number(targetAmount.value) || 0;
      const pv = Number(startingBalance.value) || 0;
      const r = periodRate.value;

      if (fv <= 0) return { valid: false, reason: 'no-target' };
      if (pv >= fv) return { valid: false, reason: 'already-there' };

      let n = null;
      let pmt = null;

      if (solveMode.value === 'amount') {
        n = periodsToDate.value;
        if (!n || n <= 0) return { valid: false, reason: 'no-date' };
        pmt = solvePmt(fv, pv, n, r);
        if (pmt === null || !isFinite(pmt) || pmt < 0) {
          return { valid: false, reason: 'unreachable' };
        }
      } else if (solveMode.value === 'date') {
        pmt = Number(contributionPerPeriod.value) || 0;
        if (pmt <= 0 && r === 0)
          return { valid: false, reason: 'no-contribution' };
        n = solveN(fv, pv, pmt, r);
        if (!isFinite(n)) {
          return { valid: false, reason: 'unreachable-date' };
        }
      } else {
        pmt = Number(contributionPerPeriod.value) || 0;
        const months = Number(goalMonths.value) || 0;
        if (pmt <= 0) return { valid: false, reason: 'no-contribution' };
        if (months <= 0) return { valid: false, reason: 'no-months' };
        n = Math.round((months * periodsPerYear.value) / 12);
      }

      const sched = buildSchedule(pv, pmt, n, r, fv);

      return {
        valid: true,
        n,
        pmt,
        ...sched,
      };
    });

    const monthlyEquivalent = computed(() => {
      if (!result.value.valid) return 0;
      return (result.value.pmt * periodsPerYear.value) / 12;
    });

    const resultEyebrow = computed(() => {
      if (!result.value.valid) return '';
      if (solveMode.value === 'amount') return 'Save this much';
      if (solveMode.value === 'date') return "You'll get there";
      return "Here's where this lands";
    });

    const resultMainText = computed(() => {
      if (!result.value.valid) return '';
      if (solveMode.value === 'amount') {
        return `$${formatNumber(result.value.pmt, 2)} per ${periodWord.value}`;
      }
      if (solveMode.value === 'date') {
        const months = Math.ceil((result.value.n / periodsPerYear.value) * 12);
        const date = new Date();
        date.setMonth(date.getMonth() + months);
        return date.toLocaleDateString(undefined, {
          month: 'long',
          year: 'numeric',
        });
      }
      const reaches =
        result.value.finalBalance >= (Number(targetAmount.value) || 0);
      if (reaches) {
        return `You'd hit $${formatNumber(Number(targetAmount.value))}`;
      }
      return `You'd reach $${formatNumber(result.value.finalBalance)}`;
    });

    const resultSecondary = computed(() => {
      if (!result.value.valid) return '';
      if (solveMode.value === 'amount') {
        return `About $${formatNumber(monthlyEquivalent.value)} per month`;
      }
      if (solveMode.value === 'date') {
        const months = Math.ceil((result.value.n / periodsPerYear.value) * 12);
        const yrs = Math.floor(months / 12);
        const mo = months % 12;
        if (yrs && mo) return `${yrs} yr ${mo} mo from now`;
        if (yrs) return `${yrs} year${yrs > 1 ? 's' : ''} from now`;
        return `${mo} month${mo === 1 ? '' : 's'} from now`;
      }
      const target = Number(targetAmount.value) || 0;
      const finalBal = result.value.finalBalance;
      const diff = finalBal - target;
      if (diff >= 0) {
        return `That's $${formatNumber(diff)} past your target.`;
      }
      const pct = Math.round((finalBal / target) * 100);
      return `That's ${pct}% of the way. Short by $${formatNumber(-diff)}.`;
    });

    const signals = computed(() => {
      const out = [];
      if (!result.value.valid) return out;

      if (selectedBudgetNode.value && budgetAvailable.value !== null) {
        const monthly = monthlyEquivalent.value;
        const available = budgetAvailable.value;
        const sourceLabel = budgetTriggerLabel.value;
        if (monthly > available) {
          out.push({
            tone: 'warn',
            icon: 'triangle-exclamation',
            title: 'Asks more than your category has',
            detail: `This plan needs $${formatNumber(
              monthly
            )}/mo but ${sourceLabel} only has $${formatNumber(
              available
            )} left this month.`,
          });
        } else {
          out.push({
            tone: 'good',
            icon: 'circle-check',
            title: 'Fits your funding source',
            detail: `$${formatNumber(
              monthly
            )}/mo fits inside ${sourceLabel} ($${formatNumber(
              available
            )} left).`,
          });
        }
      }

      if (solveMode.value !== 'goal' && result.value.n) {
        const yrs = result.value.n / periodsPerYear.value;
        if (yrs > 30) {
          out.push({
            tone: 'info',
            icon: 'circle-info',
            title: 'This is a 30+ year plan',
            detail:
              'Long horizons make sense for retirement, less so for most goals. Consider raising your contribution.',
          });
        }
      }

      if (result.value.totalInterest > 0 && result.value.totalContributed > 0) {
        const ratio =
          result.value.totalInterest / result.value.totalContributed;
        if (ratio >= 0.1) {
          out.push({
            tone: 'good',
            icon: 'arrow-trend-up',
            title: 'Interest does real work here',
            detail: `Earning $${formatNumber(
              result.value.totalInterest
            )} cuts what you actually have to set aside.`,
          });
        }
      }

      return out;
    });

    const resultToneClass = computed(() => {
      if (!result.value.valid) return '';
      if (solveMode.value === 'goal') {
        const target = Number(targetAmount.value) || 0;
        const reaches = result.value.finalBalance >= target;
        return reaches ? 'tone-good' : 'tone-warn';
      }
      const overBudget = signals.value.some((s) => s.tone === 'warn');
      return overBudget ? 'tone-warn' : 'tone-good';
    });

    const showMilestones = ref(false);
    const showSchedule = ref(false);

    const emptyMessage = computed(() => {
      const r = result.value;
      if (r.valid) return '';
      if (r.reason === 'no-target') return 'Enter a target amount to start.';
      if (r.reason === 'already-there')
        return "You're already at or past your target. Set a bigger goal or save for the next thing.";
      if (r.reason === 'no-date') return 'Pick a target date.';
      if (r.reason === 'no-contribution')
        return `Enter what you can save per ${periodWord.value}.`;
      if (r.reason === 'no-months')
        return 'Enter how many months you want to plan for.';
      if (r.reason === 'unreachable')
        return 'That date is too soon for this target. Try a later date or a bigger starting balance.';
      if (r.reason === 'unreachable-date')
        return 'Your contribution is too small to reach this target. Increase it or add a starting balance.';
      return 'Fill in a few more fields to see your plan.';
    });

    const formatNumber = (n, decimals = 0) => {
      if (n == null || isNaN(n) || !isFinite(n)) return '0';
      return Number(n).toLocaleString('en-US', {
        maximumFractionDigits: decimals,
        minimumFractionDigits: 0,
      });
    };

    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
      fetchMonthlyBudget();
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside);
    });

    return {
      router,
      goalName,
      targetAmount,
      startingBalance,
      apyPercent,
      solveMode,
      solveModes,
      payFrequency,
      freqDropdownOpen,
      freqDropdownRef,
      frequencyOptions,
      selectedFreqLabel,
      periodWord,
      selectFrequency,
      targetDate,
      contributionPerPeriod,
      goalMonths,
      minDate,
      inputsLabel,
      loadingBudget,
      spendingCategories,
      parentDisplayAvailable,
      selectedBudgetNode,
      budgetDropdownOpen,
      budgetDropdownRef,
      budgetTriggerLabel,
      selectBudgetNone,
      selectBudgetCategory,
      selectBudgetSubcategory,
      result,
      resultEyebrow,
      resultMainText,
      resultSecondary,
      resultToneClass,
      signals,
      showMilestones,
      showSchedule,
      emptyMessage,
      formatNumber,
    };
  },
};
</script>

<style scoped>
.savings-view-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.input {
  width: 100%;
}

.back-arrow {
  font-size: 18px;
  color: var(--color-primary);
  cursor: pointer;
  transition: transform var(--transition-fast);
}
.back-arrow:hover {
  transform: translateX(-2px);
}
.dark-theme .back-arrow {
  color: var(--color-accent-teal);
}

.intro-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--purple-50);
  border-radius: var(--radius-lg);
  padding: 0.85rem 1rem;
  margin-bottom: 1.25rem;
  border: 1px solid #ece9fb;
}
.dark-theme .intro-card {
  background: #1e1a40;
  border-color: #2a2060;
}
.intro-icon {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-sm);
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-accent-purple);
  font-size: 14px;
  flex-shrink: 0;
}
.dark-theme .intro-icon {
  background: #2a2060;
}
.intro-text {
  font-size: 0.8rem;
  color: #213547;
  margin: 0;
  line-height: 1.4;
}
.dark-theme .intro-text {
  color: #d4d2d2;
}

.optional-tag {
  font-size: 0.65rem;
  font-weight: 500;
  color: #9ca3af;
  text-transform: lowercase;
  letter-spacing: 0;
  margin-left: 4px;
  font-style: italic;
}

.field-hint {
  font-size: 0.7rem;
  margin: 0.4rem 0 0;
  line-height: 1.4;
}
.light-theme {
  color: #6b7280;
}

.currency-input,
.percent-input {
  position: relative;
}
.currency-symbol {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1em;
  font-weight: 600;
  color: #6b7280;
  pointer-events: none;
  z-index: 1;
}
.percent-symbol {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1em;
  font-weight: 600;
  color: #6b7280;
  pointer-events: none;
  z-index: 1;
}
.dark-theme .currency-symbol,
.dark-theme .percent-symbol {
  color: #9aa6b8;
}
.currency-field {
  padding-left: 1.85rem !important;
}
.percent-field {
  padding-right: 1.85rem !important;
}

.solve-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}
@media (min-width: 480px) {
  .solve-grid {
    grid-template-columns: 1fr 1fr 1fr;
  }
}
.solve-card {
  background: white;
  border: 2px solid #f0f0f0;
  border-radius: var(--radius-lg);
  padding: 1rem 0.85rem;
  text-align: left;
  cursor: pointer;
  transition: all var(--transition-fast);
  font-family: 'Poppins', sans-serif;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.solve-card:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}
.solve-card.selected {
  border-color: var(--color-accent-purple);
  background: var(--purple-50);
}
.dark-theme .solve-card {
  background: #212e3e;
  border-color: #2a3a4e;
}
.dark-theme .solve-card.selected {
  background: #1e1a40;
  border-color: var(--color-accent-purple);
}
.solve-icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
}
.solve-icon-wrap.purple {
  background: var(--purple-50);
  color: var(--color-accent-purple);
}
.solve-icon-wrap.teal {
  background: var(--teal-50);
  color: var(--color-accent-teal);
}
.solve-icon-wrap.coral {
  background: #fdeee7;
  color: #d85a30;
}
.dark-theme .solve-icon-wrap.purple {
  background: #2a2060;
}
.dark-theme .solve-icon-wrap.teal {
  background: #0d2420;
}
.dark-theme .solve-icon-wrap.coral {
  background: #2e1d18;
}
.solve-name {
  font-size: 0.85rem;
  font-weight: 700;
  color: #213547;
  margin: 0;
}
.solve-card.selected .solve-name {
  color: var(--color-accent-purple);
}
.dark-theme .solve-name {
  color: white;
}
.solve-desc {
  font-size: 0.7rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.35;
}
.dark-theme .solve-desc {
  color: #9aa6b8;
}

.category-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  background-image: none !important;
  padding-right: 14px !important;
  font-family: 'Poppins', sans-serif;
  color: #213547;
}
.dark-theme .category-trigger {
  color: #d4d2d2;
}
.category-trigger.open {
  border-color: var(--color-accent-purple) !important;
  box-shadow: 0 0 0 3px rgba(127, 119, 221, 0.1);
}
.category-trigger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.category-trigger-value {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.category-trigger-value.placeholder {
  color: #9ca3af;
}
.category-chevron {
  font-size: 11px;
  color: #6b7280;
  transition: transform var(--transition-fast);
  flex-shrink: 0;
}
.category-chevron.open {
  transform: rotate(180deg);
}

.category-menu {
  margin-top: 6px;
  background: white;
  border: 1.5px solid #e7e7e7;
  border-radius: var(--radius-sm);
  overflow: hidden;
  max-height: 320px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.dark-theme .category-menu {
  background: #212e3e;
  border-color: #2a3a4e;
}
.category-option {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0.6rem 0.85rem;
  background: transparent;
  border: none;
  border-bottom: 1px solid #f4f4f6;
  border-radius: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  color: #213547;
  text-align: left;
  cursor: pointer;
}
.category-option:last-child {
  border-bottom: none;
}
.category-option:hover {
  background: #fafafa;
}
.category-option.selected {
  background: var(--purple-50);
  color: var(--color-accent-purple);
  font-weight: 600;
}
.dark-theme .category-option {
  color: #d4d2d2;
  border-bottom-color: #2a3a4e;
}
.dark-theme .category-option:hover {
  background: #1a2535;
}
.dark-theme .category-option.selected {
  background: #1e1a40;
}
.category-parent {
  font-weight: 600;
}
.category-sub {
  padding-left: 1.5rem;
  font-size: 0.82rem;
  font-weight: 400;
  background: #fafbfc;
}
.dark-theme .category-sub {
  background: #1a2535;
}
.sub-indent {
  color: #c0c0c8;
  font-size: 0.75rem;
}
.dark-theme .sub-indent {
  color: #4a5a6e;
}
.option-name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.option-remaining {
  font-size: 0.7rem;
  color: #6b7280;
  flex-shrink: 0;
  font-weight: 500;
}
.dark-theme .option-remaining {
  color: #9aa6b8;
}
.category-option.selected .option-remaining {
  color: var(--color-accent-purple);
}
.category-empty {
  padding: 1rem;
  text-align: center;
  font-size: 0.8rem;
  color: #9ca3af;
}

.results-card {
  background: linear-gradient(135deg, var(--purple-50) 0%, var(--teal-50) 100%);
  border-radius: var(--radius-lg);
  padding: 1.25rem 1.15rem;
  border: 1px solid #ece9fb;
  animation: cardSlideUp 0.4s cubic-bezier(0.34, 1.2, 0.64, 1) both;
}
.dark-theme .results-card {
  background: linear-gradient(135deg, #1e1a40 0%, #0d2420 100%);
  border-color: #2a3a4e;
}
.results-card.tone-warn {
  background: linear-gradient(135deg, #fff8e6 0%, var(--purple-50) 100%);
  border-color: #f4e4b8;
}
.dark-theme .results-card.tone-warn {
  background: linear-gradient(135deg, #2e2718 0%, #1e1a40 100%);
  border-color: #4a3e1f;
}
.result-eyebrow {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #6b7280;
}
.dark-theme .result-eyebrow {
  color: #9aa6b8;
}
.result-main {
  font-family: 'Poppins', sans-serif;
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--color-primary);
  letter-spacing: -0.02em;
  margin-top: 4px;
  line-height: 1.2;
}
.dark-theme .result-main {
  color: white;
}
.result-sub {
  font-size: 0.82rem;
  color: #6b7280;
  margin-top: 4px;
}
.dark-theme .result-sub {
  color: #9aa6b8;
}

.signals-list {
  margin-top: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.signal-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 0.7rem 0.85rem;
  border-radius: var(--radius-sm);
  border: 1px solid transparent;
}
.signal-good {
  background: #e8f5ee;
  border-color: #c5e6d3;
}
.signal-warn {
  background: #fff8e6;
  border-color: #f4e4b8;
}
.signal-info {
  background: var(--purple-50);
  border-color: #ece9fb;
}
.dark-theme .signal-good {
  background: #0f2a1c;
  border-color: #1d4030;
}
.dark-theme .signal-warn {
  background: #2e2718;
  border-color: #4a3e1f;
}
.dark-theme .signal-info {
  background: #1e1a40;
  border-color: #2a2060;
}
.signal-icon {
  font-size: 14px;
  flex-shrink: 0;
  margin-top: 2px;
}
.signal-good .signal-icon {
  color: var(--color-accent-green);
}
.signal-warn .signal-icon {
  color: #8a6300;
}
.signal-info .signal-icon {
  color: var(--color-accent-purple);
}
.dark-theme .signal-good .signal-icon {
  color: var(--color-accent-teal);
}
.dark-theme .signal-warn .signal-icon {
  color: #e6cf80;
}
.signal-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}
.signal-title {
  font-size: 0.78rem;
  font-weight: 700;
  color: #213547;
}
.dark-theme .signal-title {
  color: white;
}
.signal-detail {
  font-size: 0.72rem;
  color: #4b5563;
  line-height: 1.4;
}
.dark-theme .signal-detail {
  color: #cbd5e1;
}

.milestones-card {
  padding: 0 !important;
  overflow: hidden;
}
.expandable-header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.15rem;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  text-align: left;
}
.expandable-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--color-primary);
}
.dark-theme .expandable-title {
  color: white;
}
.expandable-chevron {
  font-size: 12px;
  color: #6b7280;
  transition: transform var(--transition-fast);
}
.expandable-chevron.open {
  transform: rotate(180deg);
}

.milestones-body {
  padding: 0 1.15rem 1rem;
}
.milestone-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 0.65rem 0;
  border-top: 1px solid #f4f4f6;
}
.dark-theme .milestone-row {
  border-top-color: #2a3a4e;
}
.milestone-info {
  flex: 1;
  min-width: 0;
}
.milestone-name {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: #213547;
  margin-bottom: 2px;
}
.dark-theme .milestone-name {
  color: white;
}
.milestone-meta {
  display: block;
  font-size: 0.7rem;
  color: #6b7280;
}
.dark-theme .milestone-meta {
  color: #9aa6b8;
}
.milestone-date {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--color-accent-purple);
  flex-shrink: 0;
}
.milestone-date-unpaid {
  color: #9ca3af;
}

.schedule-body {
  padding: 0 1.15rem 1rem;
}
.schedule-table-wrap {
  max-height: 400px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid #f0f0f0;
  border-radius: var(--radius-sm);
}
.dark-theme .schedule-table-wrap {
  border-color: #2a3a4e;
}
.schedule-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
}
.schedule-table th {
  position: sticky;
  top: 0;
  background: #f8f8fb;
  text-align: left;
  padding: 0.55rem 0.7rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.65rem;
  border-bottom: 1px solid #e7e7e7;
}
.dark-theme .schedule-table th {
  background: #1a2535;
  color: #9aa6b8;
  border-bottom-color: #2a3a4e;
}
.schedule-table td {
  padding: 0.5rem 0.7rem;
  border-bottom: 1px solid #f4f4f6;
  color: #213547;
}
.dark-theme .schedule-table td {
  color: #d4d2d2;
  border-bottom-color: #2a3a4e;
}
.schedule-table tr:last-child td {
  border-bottom: none;
}
.schedule-milestone {
  background: #e8f5ee;
}
.dark-theme .schedule-milestone {
  background: #0f2a1c;
}
.text-right {
  text-align: right;
}

.interest-summary {
  background: var(--teal-50);
  border-radius: var(--radius-lg);
  padding: 0.85rem 1rem;
  border: 1px solid #d3ede0;
}
.dark-theme .interest-summary {
  background: #0d2420;
  border-color: #1d4030;
}
.interest-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 0.8rem;
  padding: 0.3rem 0;
  color: #4b5563;
}
.dark-theme .interest-row {
  color: #cbd5e1;
}
.interest-row strong {
  color: var(--color-primary);
  font-weight: 700;
}
.dark-theme .interest-row strong {
  color: white;
}
.interest-earned strong {
  color: var(--color-accent-green);
}
.dark-theme .interest-earned strong {
  color: var(--color-accent-teal);
}
.interest-final {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  margin-top: 0.3rem;
  padding-top: 0.55rem;
  font-size: 0.88rem;
}
.dark-theme .interest-final {
  border-top-color: rgba(255, 255, 255, 0.08);
}
.interest-final strong {
  font-family: 'Poppins', sans-serif;
}

.empty-card {
  background: #fafbfc;
  border: 1.5px dashed #e7e7e7;
  border-radius: var(--radius-lg);
  padding: 2rem 1.5rem;
  text-align: center;
}
.dark-theme .empty-card {
  background: #1a2535;
  border-color: #2a3a4e;
}
.empty-text {
  font-size: 0.82rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}
.dark-theme .empty-text {
  color: #9aa6b8;
}
</style>
